import { useCallback, useEffect, useMemo, useState } from "react";

import { apiClient } from "../../api/client";
import type { FeedItem, FeedResponse } from "../../api/types";
import { AddressSearch } from "../../components/AddressSearch";
import { useAuth } from "../../context/AuthContext";
import { categoryMatchesSelection, PLACE_CATEGORY_OPTIONS } from "../../lib/placeCategories";

const CIN_LAT = 39.1031;
const CIN_LNG = -84.512;
const MI_TO_KM = 1.60934;

async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=14`,
      { headers: { "Accept-Language": "en", "User-Agent": "nutoverdose-app/0.1" } },
    );
    const data = (await res.json()) as { address?: { neighbourhood?: string; suburb?: string; city?: string; town?: string } };
    const a = data.address ?? {};
    return a.neighbourhood ?? a.suburb ?? a.city ?? a.town ?? "Your Location";
  } catch {
    return "Your Location";
  }
}

const STATUS_FILTERS: { key: "all" | "open" | "likely" | "closed"; label: string }[] = [
  { key: "all", label: "All Spots" },
  { key: "open", label: "Open Now" },
  { key: "likely", label: "Likely Open" },
  { key: "closed", label: "Closed" },
];

function formatDistance(meters: number): string {
  const miles = meters / 1000 / MI_TO_KM;
  if (miles < 0.1) return "< 0.1 mi";
  return `${miles.toFixed(1)} mi`;
}

function getStatusEmoji(status: FeedItem["status"]): string {
  if (status === "open") return "✅";
  if (status === "likely") return "⚡";
  if (status === "closed") return "⛔";
  return "❔";
}

function getStatusLabel(status: FeedItem["status"]): string {
  if (status === "open") return "Open Now";
  if (status === "likely") return "Likely Open";
  if (status === "closed") return "Closed";
  return "Unknown";
}

function getCategoryEmoji(category: string): string {
  const c = category.toLowerCase();
  if (c.includes("pizza")) return "🍕";
  if (c.includes("wings") || c.includes("chicken")) return "🍗";
  if (c.includes("sushi")) return "🍣";
  if (c.includes("ramen") || c.includes("noodle")) return "🍜";
  if (c.includes("bbq") || c.includes("barbecue")) return "🍖";
  if (c.includes("steak")) return "🥩";
  if (c.includes("seafood") || c.includes("fish")) return "🐟";
  if (c.includes("burger") || c.includes("sandwich")) return "🍔";
  if (c.includes("hot dog")) return "🌭";
  if (c.includes("taco") || c.includes("mex")) return "🌮";
  if (c.includes("ice cream") || c.includes("gelato")) return "🍦";
  if (c.includes("coffee") || c.includes("cafe")) return "☕";
  if (c.includes("bar") || c.includes("pub") || c.includes("brew")) return "🍺";
  if (c.includes("liquor")) return "🥃";
  if (c.includes("donut") || c.includes("dessert") || c.includes("bakery")) return "🍩";
  if (c.includes("restaurant") || c.includes("diner")) return "🍽️";
  if (c.includes("gas")) return "⛽";
  if (c.includes("pharmacy") || c.includes("drug")) return "💊";
  if (c.includes("hospital") || c.includes("clinic")) return "🏥";
  if (c.includes("bank") || c.includes("atm")) return "🏦";
  if (c.includes("hotel") || c.includes("motel")) return "🏨";
  if (c.includes("convenience")) return "🏪";
  if (c.includes("grocery") || c.includes("market") || c.includes("supermarket")) return "🛒";
  if (c.includes("shop") || c.includes("store")) return "🛍️";
  if (c.includes("smoke")) return "🚬";
  if (c.includes("salon") || c.includes("barber")) return "💇";
  if (c.includes("spa")) return "🧖";
  if (c.includes("nail")) return "💅";
  if (c.includes("bowling")) return "🎳";
  if (c.includes("movie") || c.includes("cinema")) return "🎬";
  if (c.includes("arcade")) return "🕹️";
  if (c.includes("laundry") || c.includes("laundromat")) return "🧺";
  if (c.includes("gym") || c.includes("fitness")) return "🏋️";
  if (c.includes("park")) return "🌳";
  return "📍";
}

function cleanFilterLabel(label: string): string {
  return label.replace(/^[^A-Za-z0-9]+/, "").trim();
}

function formatCategoryLabel(category: string): string {
  return category
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getTopTag(status: FeedItem["status"]): string {
  if (status === "open") return "LIVE";
  if (status === "likely") return "LIKELY OPEN";
  if (status === "closed") return "TRACKED";
  return "CHECK";
}

function getSecondaryTag(confidenceTier: FeedItem["confidence_tier"]): string {
  if (confidenceTier === "community_confirmed") return "POPULAR";
  if (confidenceTier === "user_reported") return "NEW DROP";
  return "WATCHED";
}

function getMetaLine(item: FeedItem): string {
  const distance = item.distance_meters > 0 ? formatDistance(item.distance_meters).replace("mi", "MI").toUpperCase() : "NEARBY";
  return `${distance} · ${formatCategoryLabel(item.category).toUpperCase()}`;
}

type ConfirmState = Record<string, "pending" | "done">;

export function FeedPage({ onSignInPrompt }: { onSignInPrompt: () => void }) {
  const { user } = useAuth();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [radiusMi, setRadiusMi] = useState(5);
  const [category, setCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "open" | "likely" | "closed">("all");
  const [location, setLocation] = useState({ lat: CIN_LAT, lng: CIN_LNG });
  const [locLabel, setLocLabel] = useState("Cincinnati");
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState<ConfirmState>({});

  // Request geolocation once on mount (desktop prompt)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const label = await reverseGeocode(latitude, longitude);
        setLocation({ lat: latitude, lng: longitude });
        setLocLabel(label);
      },
      () => { /* denied or unavailable — stay on default Cincinnati */ },
      { timeout: 8000 },
    );
  }, []);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        lat: String(location.lat),
        lng: String(location.lng),
        radius_km: String(radiusMi * MI_TO_KM),
        limit: "100",
      });
      const res = await apiClient.get<FeedResponse>(`/feed?${params.toString()}`);
      setItems(res.items);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [location, radiusMi]);

  useEffect(() => {
    void fetchFeed();
  }, [fetchFeed]);

  const handleAddressSelect = (lat: number, lng: number, label: string) => {
    setLocation({ lat, lng });
    setLocLabel(label);
  };

  const handleCategoryClick = (key: string) => {
    setCategory((current) => (current === key ? "" : key));
  };

  const confirm = async (placeId: string, status: "open" | "closed" | "not_sure") => {
    if (!user) {
      onSignInPrompt();
      return;
    }
    setConfirming((prev) => ({ ...prev, [placeId]: "pending" }));
    try {
      await apiClient.post(`/places/${placeId}/confirm`, { status });
      setConfirming((prev) => ({ ...prev, [placeId]: "done" }));
      setTimeout(() => void fetchFeed(), 800);
    } catch {
      setConfirming((prev) => {
        const next = { ...prev };
        delete next[placeId];
        return next;
      });
    }
  };

  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const availableCategoryOptions = useMemo(() => {
    return PLACE_CATEGORY_OPTIONS.filter((option) => {
      if (option.key === "" || option.key === "__search__") return option.key === "";
      if (option.key === category) return true;
      return items.some((item) => categoryMatchesSelection(item.category, option.key));
    });
  }, [items, category]);

  useEffect(() => {
    if (!category) return;
    const hasMatches = items.some((item) => categoryMatchesSelection(item.category, category));
    if (!hasMatches) {
      setCategory("");
    }
  }, [items, category]);

  const categoryFilteredItems = items.filter((item) => {
    if (!category) return true;
    return categoryMatchesSelection(item.category, category);
  });

  const searchFilteredItems = categoryFilteredItems.filter((item) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;
    return item.place_name.toLowerCase().includes(query) || item.category.toLowerCase().includes(query);
  });

  const visibleItems = statusFilter === "all"
    ? searchFilteredItems
    : searchFilteredItems.filter((item) => item.status === statusFilter);
  const openCount = visibleItems.filter((item) => item.status === "open").length;
  const likelyCount = visibleItems.filter((item) => item.status === "likely").length;
  const closedCount = visibleItems.filter((item) => item.status === "closed").length;
  const isFiltered = category !== "" || statusFilter !== "all" || searchTerm.trim().length > 0;

  const activeCategoryChip = category;
  const radiusLabel = `${radiusMi} mi`;
  const headerCopy = `${visibleItems.length} late-night spot${visibleItems.length === 1 ? "" : "s"} around ${locLabel}`;

  return (
    <main className="page-shell buzz-page">
      <header className="buzz-header">
        <div className="buzz-title-block">
          <span className="buzz-title-mark" aria-hidden>✺</span>
          <div>
            <h1>Near Me</h1>
            <p className="buzz-subtitle">{headerCopy}</p>
          </div>
        </div>
        <button
          type="button"
          className="loc-pill buzz-location-trigger"
          onClick={() => setShowMobileSearch((v) => !v)}
          aria-label="Change location"
        >
          ⌖
        </button>
      </header>

      <label className="buzz-search-shell" htmlFor="buzz-search">
        <span className="buzz-search-icon" aria-hidden>⌕</span>
        <input
          id="buzz-search"
          type="text"
          className="buzz-search-input"
          placeholder="Find late-night spots..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {searchTerm ? (
          <button
            type="button"
            className="buzz-search-clear"
            onClick={() => setSearchTerm("")}
            aria-label="Clear search"
          >
            ×
          </button>
        ) : null}
      </label>

      <div className={showMobileSearch ? "address-search--open buzz-location-search" : "buzz-location-search"}>
        <AddressSearch
          onSelect={(lat, lng, label) => {
            handleAddressSelect(lat, lng, label);
            setShowMobileSearch(false);
          }}
          currentLabel={locLabel}
        />
      </div>

      <section className="buzz-summary-row" aria-label="Signal summary">
        <div className="buzz-summary-pill buzz-summary-pill-open">
          <strong>{openCount}</strong>
          <span>Open</span>
        </div>
        <div className="buzz-summary-pill buzz-summary-pill-likely">
          <strong>{likelyCount}</strong>
          <span>Likely Open</span>
        </div>
        <div className="buzz-summary-pill buzz-summary-pill-closed">
          <strong>{closedCount}</strong>
          <span>Closed</span>
        </div>
      </section>

      <div className="buzz-chip-stack">
        <div className="buzz-chip-row">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`filter-chip buzz-chip buzz-chip-status${statusFilter === filter.key ? " active" : ""}`}
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="buzz-chip-row">
          {availableCategoryOptions.map((cat) => (
            <button
              key={cat.key}
              type="button"
              className={`filter-chip buzz-chip${activeCategoryChip === cat.key ? " active" : ""}`}
              onClick={() => handleCategoryClick(cat.key)}
            >
              {cleanFilterLabel(cat.label)}
            </button>
          ))}
        </div>
      </div>

      <label className="radius-input buzz-radius-card" htmlFor="radius-range">
        <div className="buzz-radius-copy">
          <span className="buzz-radius-label">Search Radius</span>
          <strong className="buzz-radius-value">{radiusLabel}</strong>
        </div>
        <input
          id="radius-range"
          type="range"
          min={1}
          max={15}
          step={1}
          value={radiusMi}
          onChange={(e) => setRadiusMi(Number(e.target.value))}
        />
      </label>

      {loading && items.length === 0 ? (
        <div className="empty-state"><p>Loading…</p></div>
      ) : visibleItems.length === 0 ? (
        <div className="empty-state">
          <p>No buzz items match this filter right now.</p>
          {isFiltered && <p style={{ marginTop: "8px" }}>Try removing filters or widening radius.</p>}
        </div>
      ) : (
        <ul className="buzz-list">
          {visibleItems.map((item) => {
            const confirmState = confirming[item.place_id];
            const isDone = confirmState === "done";
            const isPending = confirmState === "pending";
            const categoryEmoji = getCategoryEmoji(item.category);
            const statusLabel = getStatusLabel(item.status);

            return (
              <li
                key={item.place_id}
                className={`buzz-card venue-card venue-card-${item.status}`}
              >
                <div className="venue-card-head">
                  <div className="venue-card-tags">
                    <span className={`venue-card-tag venue-card-tag-${item.status}`}>{getTopTag(item.status)}</span>
                    <span className="venue-card-tag venue-card-tag-secondary">{getSecondaryTag(item.confidence_tier)}</span>
                  </div>
                  <div className="venue-card-icon" aria-hidden>{categoryEmoji}</div>
                </div>

                <div className="buzz-card-content venue-card-content">
                  <div className="buzz-card-head venue-card-title-row">
                    <p className="buzz-place venue-card-name">
                      <span className="venue-card-type-emoji" aria-hidden>{categoryEmoji}</span>
                      <span>{item.place_name}</span>
                    </p>
                  </div>
                  <p className="venue-card-meta">{getMetaLine(item)}</p>
                  <div className={`venue-card-status-panel venue-card-status-panel-${item.status}`}>
                    <span className="venue-card-status-kicker">Current Status</span>
                    <strong className="venue-card-status-value">
                      <span aria-hidden>{getStatusEmoji(item.status)}</span>
                      <span>{statusLabel}</span>
                    </strong>
                  </div>
                  <p className="buzz-card-note venue-card-note">{item.last_signal_text}</p>
                </div>

                <div className="buzz-actions venue-card-actions">
                  {isDone ? (
                    <span className="buzz-confirmed venue-card-confirmed">✓ Signal added</span>
                  ) : (
                    <>
                      <button type="button" className="btn-open" disabled={isPending}
                        onClick={() => void confirm(item.place_id, "open")}>
                        Open
                      </button>
                      <button type="button" className="btn-closed" disabled={isPending}
                        onClick={() => void confirm(item.place_id, "closed")}>
                        Closed
                      </button>
                      <button type="button" className="btn-unsure" disabled={isPending}
                        onClick={() => void confirm(item.place_id, "not_sure")}>
                        Unsure
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
